/**
 * Parses a tennis match score string entered from the player's perspective.
 * The first number in each set is the player's games, second is the opponent's.
 *
 * Supported formats per set: "6-3", "6/3", "6 - 3", "6 3"
 * Sets are separated by commas: "6-3, 7-5" or "6-3 7-5"
 */
export function parseMatchScore(score: string): {
  playerSets: number;
  opponentSets: number;
  playerGames: number;
  opponentGames: number;
} | null {
  if (!score.trim()) return null;

  // Split sets by comma; fall back to splitting all set-shaped tokens
  const setStrings = score
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (setStrings.length === 0) return null;

  let playerSets = 0;
  let opponentSets = 0;
  let playerGames = 0;
  let opponentGames = 0;

  for (const set of setStrings) {
    const match = set.match(/^(\d+)\s*[-\/\s]\s*(\d+)/);
    if (!match) return null;

    const p = parseInt(match[1], 10);
    const o = parseInt(match[2], 10);

    playerGames += p;
    opponentGames += o;

    if (p > o) playerSets++;
    else if (o > p) opponentSets++;
    // tied games in a set (e.g. 6-6 without tiebreak) — neither gets the set
  }

  return { playerSets, opponentSets, playerGames, opponentGames };
}

/**
 * Derives win/loss outcome from the score string.
 * Returns null if the score is unparseable or the sets are tied (incomplete match).
 */
export function deriveOutcome(score: string): 'win' | 'loss' | null {
  const parsed = parseMatchScore(score);
  if (!parsed) return null;
  if (parsed.playerSets > parsed.opponentSets) return 'win';
  if (parsed.opponentSets > parsed.playerSets) return 'loss';
  return null; // tied sets — incomplete or invalid match score
}
